import React from 'react';
import { Collapse, Empty, Tag } from 'antd';
import { FONT_SIZE, FONT_WEIGHT, LINE_HEIGHT, SPACING, type ThemeTokens } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { AppModal } from '../common/AppModal';
import { formatVersion, SECTION_ORDER, type ChangelogEntry } from './changelogData';

// ─── 절 태그 색 ───────────────────────────────────────────────────────────────
//
// Ant 의 color 프리셋(blue/orange…)은 쓰지 않는다 — 디자인 시스템 토큰으로만 칠한다
// (DESIGN_SYSTEM.md §5~6). Added 초록 · Changed 파랑 · Fixed 주황 · Security 빨강 ·
// Removed 회색, 모르는 절은 중립 회색.

function sectionTagStyle(name: string, t: ThemeTokens): React.CSSProperties {
  const base: React.CSSProperties = {
    margin: 0,
    marginBottom: SPACING.xxs,
    fontSize: FONT_SIZE.xs,
  };

  switch (name) {
    case 'Added':
      return { ...base, background: t.tintSuccess, color: t.success, borderColor: t.lineSuccess };
    case 'Changed':
      return { ...base, background: t.tintInfo, color: t.accent, borderColor: t.lineAccent };
    case 'Fixed':
      return { ...base, background: t.tintWarning, color: t.warning, borderColor: t.lineWarning };
    case 'Security':
      return { ...base, background: t.tintDanger, color: t.danger, borderColor: t.lineDanger };
    case 'Removed':
      return { ...base, background: t.bgRaised, color: t.textSecondary, borderColor: t.border };
    default:
      return { ...base, background: t.bgRaised, color: t.textSecondary, borderColor: t.border };
  }
}

/** 알려진 절을 먼저, 모르는 절은 원래 순서대로 뒤에. */
function orderedSections(entry: ChangelogEntry): string[] {
  const keys = Object.keys(entry.sections || {});
  return [
    ...SECTION_ORDER.filter((name) => keys.includes(name)),
    ...keys.filter((name) => !SECTION_ORDER.includes(name)),
  ];
}

const EntryHeader: React.FC<{ entry: ChangelogEntry }> = ({ entry }) => {
  const t = useThemeTokens();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: SPACING.xs,
        marginBottom: SPACING.sm,
      }}
    >
      <span style={{ fontSize: FONT_SIZE.md, fontWeight: FONT_WEIGHT.bold, color: t.textPrimary }}>
        {formatVersion(entry.version)}
      </span>
      {entry.date && (
        <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>{entry.date}</span>
      )}
    </div>
  );
};

/**
 * 괄호 밖의 `. ! ?` + 공백에서 첫 문장과 나머지로 나눈다(prebuild 의 splitSentences 와 같은 기준 — `0.36.0` 은 안 나눈다).
 * 이력 한 줄이 길어 최신 버전 하나가 창을 다 덮었다(2026-10-21 코덱 화면 점검, 감사 card-controltower#65) — 첫 문장(무엇이
 * 바뀌었나)만 보이고 나머지(왜 · 어떻게)는 "자세히" 로 접는다.
 */
export function splitFirstSentence(line: string): { head: string; rest: string } {
  let depth = 0;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '(') depth += 1;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    else if (depth === 0 && /[.!?]/.test(ch) && /\s/.test(line[i + 1] || '')) {
      return { head: line.slice(0, i + 1).trim(), rest: line.slice(i + 1).trim() };
    }
  }
  return { head: line.trim(), rest: '' };
}

const EntryLine: React.FC<{ line: string }> = ({ line }) => {
  const t = useThemeTokens();
  const { head, rest } = splitFirstSentence(line);
  if (!rest) return <>{head}</>;
  return (
    <>
      {head}
      <details style={{ marginTop: 2 }}>
        <summary style={{ cursor: 'pointer', color: t.textMuted, fontSize: FONT_SIZE.xs }}>자세히</summary>
        <div style={{ marginTop: SPACING.xxs }}>{rest}</div>
      </details>
    </>
  );
};

const EntrySections: React.FC<{ entry: ChangelogEntry }> = ({ entry }) => {
  const t = useThemeTokens();
  const sections = orderedSections(entry);

  if (sections.length === 0) {
    return <div style={{ fontSize: FONT_SIZE.sm, color: t.textMuted }}>기록된 항목이 없습니다.</div>;
  }

  return (
    <>
      {sections.map((name) => (
        <div key={name} style={{ marginBottom: SPACING.md }}>
          <Tag style={sectionTagStyle(name, t)}>{name}</Tag>
          <ul
            style={{
              margin: 0,
              paddingLeft: SPACING.lg,
              color: t.textSecondary,
              fontSize: FONT_SIZE.sm,
              lineHeight: LINE_HEIGHT.relaxed,
            }}
          >
            {(entry.sections[name] || []).map((line, index) => (
              <li key={index} style={{ marginBottom: SPACING.xxs }}><EntryLine line={line} /></li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
};

const CollapseLabel: React.FC<{ entry: ChangelogEntry }> = ({ entry }) => {
  const t = useThemeTokens();
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: SPACING.xs }}>
      <span style={{ fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary }}>
        {formatVersion(entry.version)}
      </span>
      {entry.date && (
        <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>{entry.date}</span>
      )}
    </span>
  );
};

// ─── UpdateHistoryModal ───────────────────────────────────────────────────────

export interface UpdateHistoryModalProps {
  open: boolean;
  onClose: () => void;
  /** 최신 버전이 앞에 오는 순서(prebuild 가 CHANGELOG 순서 그대로 만든다). */
  entries: ChangelogEntry[];
  /** 생성 JSON 을 읽지 못했는지 — 빈 목록(이력 없음)과 구분해 안내한다. */
  loadFailed?: boolean;
  /**
   * 아직 확인하지 않은 새 버전인지 — 제목을 "새 업데이트 · vX" 로. 사이드바의 빨간 점은 자동으로 뜬 이 창 뒤에 가려졌다가
   * 닫는 순간 사라져 사실상 안 보였다(감사 card-controltower#65) — 새 버전이라는 것을 창 제목이 직접 알린다.
   */
  isNew?: boolean;
}

/**
 * 사이드바 버전 옆 ⓘ 버튼으로 여는 업데이트 이력 모달.
 * 최신 버전은 펼쳐서, 이전 버전은 Collapse 로 접어서 보여준다.
 */
export const UpdateHistoryModal: React.FC<UpdateHistoryModalProps> = ({
  open,
  onClose,
  entries,
  loadFailed = false,
  isNew = false,
}) => {
  const latest = entries[0];
  const older = entries.slice(1);

  return (
    <AppModal
      title={isNew && latest ? `새 업데이트 · ${formatVersion(latest.version)}` : '업데이트 이력'}
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      styles={{ body: { maxHeight: '60vh', overflowY: 'auto' } }}
    >
      {loadFailed ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="업데이트 이력을 불러오지 못했습니다."
        />
      ) : !latest ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="아직 등록된 업데이트 이력이 없습니다."
        />
      ) : (
        <>
          <div>
            <EntryHeader entry={latest} />
            <EntrySections entry={latest} />
          </div>

          {older.length > 0 && (
            <Collapse
              ghost
              items={older.map((entry) => ({
                key: entry.version,
                label: <CollapseLabel entry={entry} />,
                children: <EntrySections entry={entry} />,
              }))}
            />
          )}
        </>
      )}
    </AppModal>
  );
};
